import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import { GridColDef } from '@mui/x-data-grid-pro';

import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import TableInstance from '@/shared/constants/tableInstance';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';
import Zone from '@/shared/types/shipping/zones/zone.type';

import CustomColumn from './components/CustomColumn';
import useManageZonesTable_v2 from './hooks/useManageZonesTable_v2';

type ManageZonesTable_v2Props = {
  refreshTableIndex: number;
  shippingProfile: ShippingProfile;
  onAddZone: (key?: keyof Zone, zone?: Zone) => void;
  onUpdateRate: (value: Zone[]) => void;
  onRemoveZones: (value: Zone[]) => void;
};

const ManageZonesTable_v2: FC<ManageZonesTable_v2Props> = ({
  refreshTableIndex,
  shippingProfile,
  onAddZone,
  onUpdateRate,
  onRemoveZones,
}) => {
  const { t } = useTranslation(['shipping', 'common']);

  const { apiRef, model, table, columns, visibilityModel, setVisibilityModel } =
    useManageZonesTable_v2({ shippingProfile, refreshTableIndex });

  const customizedColumns: GridColDef[] = columns.map((column) => {
    switch (column.field) {
      case 'delivery_country':
      case 'delivery_state':
        return {
          ...column,
          renderCell(params) {
            const row = params.row as Zone;
            const key = params.field as keyof typeof row;

            return (
              <CustomColumn
                value={row[key]}
                onAddZoneClick={() => onAddZone(key, row)}
              />
            );
          },
        };
      default:
        return column;
    }
  });

  return (
    <div style={{ height: '60vh', width: '100%' }}>
      <DataGridTable
        getRowId={(item) => item.shipping_profile_zone_id as string}
        columns={customizedColumns}
        rows={table.data}
        rowCount={table.totalRows}
        loading={table.isLoading}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={setVisibilityModel}
        customProps={{
          tableInstance: TableInstance.ManageZones,
          apiRef,
          model,
          toolbar: {
            title: t('shipping:manageZones'),
            toolbarActions: (
              <>
                <Button
                  variant='contained'
                  size='large'
                  onClick={() => onAddZone()}
                >
                  {t('shipping:addZone')}
                </Button>
              </>
            ),
            rowActions: [
              {
                type: 'default',
                button: {
                  label: t('shipping:updateRate'),
                  onClick: (apiRef, rowId) => {
                    const selectedRow = apiRef.current.getRow(
                      rowId || '',
                    ) as Zone;
                    onUpdateRate([selectedRow]);
                  },
                },
              },
              {
                type: 'default',
                button: {
                  label: t('shipping:removeZone'),
                  onClick: (apiRef, rowId) => {
                    const selectedRow = apiRef.current.getRow(
                      rowId || '',
                    ) as Zone;
                    onRemoveZones([selectedRow]);
                  },
                },
              },
            ],
          },
          snackBarProps: {
            actions: [
              {
                type: 'default',
                button: {
                  label: t('shipping:updateRate'),
                  color: 'primary',
                  variant: 'text',
                  onClick: (apiRef) => {
                    const selectedRows = [
                      ...apiRef.current.getSelectedRows().values(),
                    ] as Zone[];
                    onUpdateRate(selectedRows);
                  },
                },
              },
              {
                type: 'default',
                button: {
                  label: t('common:remove'),
                  color: 'error',
                  onClick: (apiRef) => {
                    const selectedRows = [
                      ...apiRef.current.getSelectedRows().values(),
                    ] as Zone[];
                    onRemoveZones(selectedRows);
                  },
                },
              },
            ],
          },
        }}
      />
    </div>
  );
};

export default ManageZonesTable_v2;
